// =============================================================================
// Shared Logger Utility
// =============================================================================

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
	level: LogLevel;
	message: string;
	timestamp: string;
	context?: Record<string, unknown>;
	error?: Error;
	sessionId?: string;
	[key: string]: unknown;
}

export interface LoggerConfig {
	level: LogLevel;
	enableConsole: boolean;
	/** Optional callback for remote logging or buffering */
	onLog?: (entry: LogEntry) => void;
}

const LEVEL_PRIORITIES: Record<LogLevel, number> = {
	debug: 0,
	info: 1,
	warn: 2,
	error: 3,
};

export class UniversalLogger {
	protected config: LoggerConfig;

	constructor(config: LoggerConfig) {
		this.config = config;
	}

	protected shouldLog(level: LogLevel): boolean {
		return LEVEL_PRIORITIES[level] >= LEVEL_PRIORITIES[this.config.level];
	}

	protected createEntry(
		level: LogLevel,
		message: string,
		context?: Record<string, unknown>,
		error?: Error,
	): LogEntry {
		return {
			level,
			message,
			timestamp: new Date().toISOString(),
			context,
			error,
		};
	}

	protected formatConsoleMessage(entry: LogEntry): string {
		const time = new Date(entry.timestamp).toLocaleTimeString();
		const prefix = `[${time}] ${entry.level.toUpperCase()}`;
		return `${prefix}: ${entry.message}`;
	}

	protected logToConsole(entry: LogEntry): void {
		if (!this.config.enableConsole) return;

		const msg = this.formatConsoleMessage(entry);
		const args: unknown[] = entry.context ? [msg, entry.context] : [msg];
		if (entry.error) args.push(entry.error);

		switch (entry.level) {
			case 'debug':
				console.debug(...args);
				break;
			case 'info':
				console.info(...args);
				break;
			case 'warn':
				console.warn(...args);
				break;
			case 'error':
				console.error(...args);
				break;
		}
	}

	protected onLogEntry(entry: LogEntry): void {
		if (this.config.onLog) {
			this.config.onLog(entry);
		}
	}

	public log(
		level: LogLevel,
		message: string,
		context?: Record<string, unknown>,
		error?: Error,
	): void {
		if (!this.shouldLog(level)) return;

		const entry = this.createEntry(level, message, context, error);

		this.logToConsole(entry);
		this.onLogEntry(entry);
	}

	public debug(message: string, context?: Record<string, unknown>): void {
		this.log('debug', message, context);
	}

	public info(message: string, context?: Record<string, unknown>): void {
		this.log('info', message, context);
	}

	public warn(message: string, context?: Record<string, unknown>, error?: Error): void {
		this.log('warn', message, context, error);
	}

	public error(message: string, context?: Record<string, unknown>, error?: Error): void {
		this.log('error', message, context, error);
	}
}
